import React from 'react';
import { useParams } from 'react-router-dom';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

import { Form } from 'core/forms';
import { Button } from 'uikit/buttons';
import { Loading } from 'uikit/loaders';

import { addStylesToSchema } from '@/exceed';
import { LearningObjectives } from '@/courseOutline';

import VideoPlayer from './videoPlayer';
import VideosAdminContent from './videosAdminContent';

import styles from '../styles/videosAdminCompose.module.scss';
import getIsAgendaItem from '../helpers/getIsAgendaItem';

const VideosAdminCompose = ({
  courseId,
  draftState,
  shouldShowForm,
  shouldHidePlayer,
  shouldHideReplace,
  schema,
  coverArtSchema,
  model,
  isReplacing,
  isLiveVideo,
  onFormUpdate,
  onReplaceVideoClick,
  onCoverArtUpdate,
  video,
  shouldShowLoading,
  shouldShowEmptyState,
  listHeaderColumns,
  listHeaderTitle,
  listHeaderAddMenuItems,
  listEmptyComponent,
  currentTreeContent,
  onKebabMenuItemClick,
  isEditingDisabled,
  isComposeListDisabled,
}) => {
  const params = useParams();
  const replacementKey = getIsAgendaItem(params) ? 'add-video-trailer' : 'replace-video';

  return (
    <div className={styles.element}>
      {shouldShowForm ? (
        <>
          {isReplacing ? (
            <div className={styles.loader}>
              <Loading />
            </div>
          ) : (
            <div className={styles.videoPlayer}>
              {!shouldHidePlayer && (
                <VideoPlayer
                  id={params.id}
                  className={styles.video}
                  video={video.video}
                  onReplaceVideoClick={onReplaceVideoClick}
                  isReplaceVideoDisabled={isEditingDisabled}
                  shouldNotFloat
                  shouldNotAutoplay
                  shouldNotEnroll
                />
              )}
            </div>
          )}
          <div className={styles.editorWrap}>
            <div className={styles.actions}>
              <div className={styles.coverArt}>
                <Form schema={coverArtSchema} model={model} onUpdate={onCoverArtUpdate} />
              </div>
              {!shouldHideReplace && (
                <div className={styles.replaceVideo}>
                  <Button
                    size="small"
                    text={i18n.t(replacementKey)}
                    variant="outlined"
                    icon={<AddCircleOutline />}
                    onClick={() => onReplaceVideoClick(replacementKey)}
                    isDisabled={isReplacing || isEditingDisabled}
                  />
                </div>
              )}
            </div>
            <Form
              model={model}
              onUpdate={onFormUpdate}
              schema={addStylesToSchema(schema, styles)}
            />
            <LearningObjectives
              defaultObjectiveableId={courseId}
              isCourseDraft={!isEditingDisabled}
              draftState={draftState}
            />
            {isLiveVideo && (
              <VideosAdminContent
                shouldShowListView
                shouldShowListAddMenu
                shouldHideSearchBar
                isInLiveMode
                currentTreeContent={currentTreeContent}
                shouldDisableDrag
                shouldShowLoadingWheel={shouldShowLoading}
                shouldShowEmptyState={shouldShowEmptyState}
                listHeaderColumns={listHeaderColumns}
                listHeaderTitle={listHeaderTitle}
                listHeaderToggleButtons={[]}
                listHeaderAddMenuItems={listHeaderAddMenuItems}
                onKebabMenuItemClick={onKebabMenuItemClick}
                listEmptyComponent={listEmptyComponent}
                isComposeListDisabled={isComposeListDisabled}
                params={params}
              />
            )}
          </div>
        </>
      ) : (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}
    </div>
  );
};

VideosAdminCompose.defaultProps = {
  shouldShowForm: false,
  schema: {},
  model: {},
  video: {},
  onFormUpdate: () => {},
  listEmptyComponent: null,
  isEditingDisabled: false,
  isComposeListDisabled: false,
};

VideosAdminCompose.propTypes = {
  shouldShowForm: PropTypes.bool,
  schema: PropTypes.shape({}),
  model: PropTypes.shape({}),
  video: PropTypes.shape({}),
  onFormUpdate: PropTypes.func,
  listEmptyComponent: PropTypes.node,
  isEditingDisabled: PropTypes.bool,
  isComposeListDisabled: PropTypes.bool,
};

export default React.memo(VideosAdminCompose);
